import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowRight,
  BarChart3,
  Database,
  ShieldCheck,
  Cpu,
  Activity,
  Brain,
  Zap,
  Code2
} from 'lucide-react';
import { CASE_STUDIES } from '@/shared/data';
import { useI18n } from '@/shared/i18n';
import { NAVIGATION_EVENTS } from '@/shared/utils/constants';
import { hoverLift, tapPress } from '@/shared/utils/motion';
import MobileHomePage, {
  type HomeApproachItem,
  type HomeCaseItem,
  type HomeServiceItem,
  type HomeTechPartner
} from './MobileHomePage';

const SERVICE_SLUGS = [
  'data-engineering',
  'managed-data-platform',
  'business-intelligence',
  'ai-machine-learning'
];

const SERVICE_ICONS = [Cpu, Activity, BarChart3, Brain];

const FEATURED_CASE_IDS = [
  'asml-data-modernization',
  'a1-serbia-dwh-migration'
] as const;

const APPROACH_ICONS = [ShieldCheck, Code2, Zap, Database];

const HOME_SECTION_IDS = ['home', 'services', 'cases', 'approach', 'contact'];
const MOBILE_SECTION_IDS = [
  'mobile-home',
  'mobile-services',
  'mobile-cases',
  'mobile-approach',
  'mobile-contact'
];

const DESKTOP_SCROLL_MEDIA_QUERY = '(min-width: 768px)';

const sectionTransition = (delay = 0) => ({
  duration: 0.55,
  ease: [0.76, 0, 0.24, 1] as [number, number, number, number],
  delay
});

const cardLoadTransition = (delay = 0) => ({
  duration: 0.84,
  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
  times: [0, 0.36, 0.72, 1],
  delay
});

const visibleState = { opacity: 1, y: 0, scale: 1 };
const hiddenState = (y = 18) => ({ opacity: 0, y, scale: 1 });
const cardHiddenState = (y = 24) => ({ opacity: 0, y, scale: 0.96 });
const cardVisibleState = (y = 24) => ({
  opacity: [0, 1, 1, 1],
  y: [y, 8, -3, 0],
  scale: [0.96, 0.9, 1.04, 1]
});

const getInitialDesktopScrollState = () => {
  if (typeof window === 'undefined') return true;

  return window.matchMedia(DESKTOP_SCROLL_MEDIA_QUERY).matches;
};

type SectionSoftEdgeProps = {
  top?: string;
  bottom?: string;
};

const SectionSoftEdge = ({ top, bottom }: SectionSoftEdgeProps) => (
  <>
    {top ? (
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 z-[1] h-24 ${top}`}
      />
    ) : null}

    {bottom ? (
      <div
        className={`pointer-events-none absolute inset-x-0 -bottom-px z-[1] h-0 ${bottom}`}
      />
    ) : null}
  </>
);

const HomePage = () => {
  const { t } = useI18n();
  const [activeSection, setActiveSection] = useState(0);
  const [isDesktopScrollEnabled, setIsDesktopScrollEnabled] = useState(
    getInitialDesktopScrollState
  );
  const [animatedSections, setAnimatedSections] = useState<Set<number>>(
    () => new Set()
  );

  const isAnimatingRef = useRef(false);
  const animationTimeoutRef = useRef<number | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);

  const prefersReducedMotion = useReducedMotion();
  const sectionCount = HOME_SECTION_IDS.length;
  const shouldAnimateContent = isDesktopScrollEnabled && !prefersReducedMotion;
  const servicesItems: HomeServiceItem[] = t.home.services.items.map(
    (item, index) => ({
      ...item,
      icon: SERVICE_ICONS[index],
      slug: SERVICE_SLUGS[index]
    })
  );
  const featuredCases: HomeCaseItem[] = FEATURED_CASE_IDS.flatMap(caseId => {
    const project = CASE_STUDIES.find(item => item.id === caseId);

    if (!project) return [];

    const caseCopy = t.cases.studies[caseId];

    return [
      {
        title: caseCopy.title,
        metric: caseCopy.metrics[0]?.value ?? t.home.cases.titleHighlight,
        desc: caseCopy.solution,
        tag: t.cases.categoryLabels[project.category],
        icon: project.icon,
        image: project.image,
        imageClassName:
          project.image && project.id === 'asml-data-modernization'
            ? 'scale-[1.7]'
            : project.image
              ? 'scale-[1.18]'
              : undefined
      }
    ];
  });
  const approachItems: HomeApproachItem[] = t.home.approach.items.map(
    (item, index) => ({
      ...item,
      icon: APPROACH_ICONS[index]
    })
  );
  const techPartners: HomeTechPartner[] = t.common.techPartners.map(name => ({
    name
  }));

  const panelTransition = prefersReducedMotion
    ? { duration: 0 }
    : {
        duration: 0.72,
        ease: [0.76, 0, 0.24, 1] as [number, number, number, number]
      };

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_SCROLL_MEDIA_QUERY);

    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsDesktopScrollEnabled(event.matches);
    };

    setIsDesktopScrollEnabled(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  useEffect(() => {
    if (!isDesktopScrollEnabled) return;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto'
    });
  }, [isDesktopScrollEnabled]);

  const dispatchHomeSectionChange = useCallback(
    (sectionIndex: number) => {
      window.dispatchEvent(
        new CustomEvent(NAVIGATION_EVENTS.HOME_SECTION_CHANGE, {
          detail: { sectionIndex, sectionCount }
        })
      );
    },
    [sectionCount]
  );

  const goToSection = useCallback(
    (nextIndex: number, shouldDispatchEvent = true) => {
      const clampedIndex = Math.max(0, Math.min(sectionCount - 1, nextIndex));

      if (!isDesktopScrollEnabled) {
        setActiveSection(clampedIndex);

        window.requestAnimationFrame(() => {
          const targetSection = document.getElementById(
            MOBILE_SECTION_IDS[clampedIndex]
          );

          targetSection?.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        });

        if (shouldDispatchEvent) {
          dispatchHomeSectionChange(clampedIndex);
        }

        return;
      }

      if (clampedIndex === activeSection || isAnimatingRef.current) return;

      isAnimatingRef.current = true;
      setActiveSection(clampedIndex);

      if (shouldDispatchEvent) {
        dispatchHomeSectionChange(clampedIndex);
      }

      if (animationTimeoutRef.current !== null) {
        window.clearTimeout(animationTimeoutRef.current);
      }

      animationTimeoutRef.current = window.setTimeout(() => {
        isAnimatingRef.current = false;
        animationTimeoutRef.current = null;
      }, 850);
    },
    [
      activeSection,
      dispatchHomeSectionChange,
      isDesktopScrollEnabled,
      prefersReducedMotion,
      sectionCount
    ]
  );

  useEffect(() => {
    setAnimatedSections(previousSections => {
      if (previousSections.has(activeSection)) {
        return previousSections;
      }

      const nextSections = new Set(previousSections);
      nextSections.add(activeSection);
      return nextSections;
    });
  }, [activeSection]);

  useEffect(() => {
    const handleHomeSectionChange = (event: Event) => {
      const sectionEvent = event as CustomEvent<{ sectionIndex: number }>;

      if (typeof sectionEvent.detail?.sectionIndex !== 'number') return;

      goToSection(sectionEvent.detail.sectionIndex, false);
    };

    dispatchHomeSectionChange(activeSection);

    window.addEventListener(
      NAVIGATION_EVENTS.HOME_SECTION_CHANGE,
      handleHomeSectionChange
    );

    return () => {
      window.removeEventListener(
        NAVIGATION_EVENTS.HOME_SECTION_CHANGE,
        handleHomeSectionChange
      );
    };
  }, [activeSection, dispatchHomeSectionChange, goToSection]);

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current !== null) {
        window.clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!isDesktopScrollEnabled) return;

      event.preventDefault();

      if (Math.abs(event.deltaY) < 24) return;

      goToSection(activeSection + (event.deltaY > 0 ? 1 : -1));
    },
    [activeSection, goToSection, isDesktopScrollEnabled]
  );

  useEffect(() => {
    const mainElement = mainRef.current;

    if (!mainElement) return;

    mainElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      mainElement.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const getSectionAnimationState = (sectionIndex: number, y = 18) => {
    if (!shouldAnimateContent) {
      return visibleState;
    }

    const shouldBeVisible =
      activeSection === sectionIndex || animatedSections.has(sectionIndex);

    return shouldBeVisible ? visibleState : hiddenState(y);
  };

  const getCardAnimationState = (sectionIndex: number, y = 24) => {
    if (!shouldAnimateContent) {
      return visibleState;
    }

    const shouldBeVisible =
      activeSection === sectionIndex || animatedSections.has(sectionIndex);

    return shouldBeVisible ? cardVisibleState(y) : cardHiddenState(y);
  };

  return (
    <main
      ref={mainRef}
      className="relative min-h-screen overflow-x-hidden bg-background-primary text-text-primary md:h-[100dvh] md:overflow-hidden"
    >
      <MobileHomePage
        servicesItems={servicesItems}
        featuredCases={featuredCases}
        approachItems={approachItems}
        techPartners={techPartners}
        goToSection={nextIndex => goToSection(nextIndex)}
      />

      <motion.div
        className="hidden h-full will-change-transform md:block"
        animate={
          isDesktopScrollEnabled
            ? { y: `-${activeSection * 100}dvh` }
            : { y: 0 }
        }
        transition={panelTransition}
      >
        {/* ===== SECTION 1: HERO ===== */}
        <section
          id="home"
          className="relative flex h-[100dvh] flex-col justify-between overflow-hidden px-5 pb-16 pt-28 text-[#101726] sm:px-12 sm:pb-20 sm:pt-32 md:pb-16 md:pt-28 lg:pt-40 dark:text-white"
        >
          <div className="absolute inset-0 bg-[linear-gradient(115deg,#edf5ff_0%,#f7fbff_42%,#d9eef4_100%)] dark:bg-[linear-gradient(115deg,#10172b_0%,#131630_38%,#0b3141_100%)]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[600px] bg-[radial-gradient(circle_at_50%_-10%,rgba(0,187,195,0.12),transparent_50%)] dark:bg-[radial-gradient(circle_at_50%_-10%,rgba(0,187,195,0.2),transparent_50%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(14,27,52,.045)_1px,transparent_1px),linear-gradient(90deg,rgba(14,27,52,.045)_1px,transparent_1px)] bg-[size:96px_96px] dark:bg-[linear-gradient(rgba(255,255,255,.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.025)_1px,transparent_1px)]" />

          <SectionSoftEdge bottom="bg-gradient-to-b from-transparent via-[#edf5ff]/25 to-[#f4f8fc]/75 dark:via-[#10172b]/30 dark:to-[#090f1c]/70" />

          <motion.div
            className="relative z-10 mx-auto flex w-full max-w-6xl flex-grow flex-col justify-center space-y-5 sm:space-y-8"
            initial={shouldAnimateContent ? hiddenState(18) : false}
            animate={getSectionAnimationState(0, 18)}
            transition={sectionTransition(0)}
          >
            <div className="max-w-4xl space-y-3 sm:space-y-4">
              <span className="text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] sm:text-xs dark:text-[#00bbc3]">
                {t.home.hero.eyebrow}
              </span>

              <h1 className="text-balance text-[clamp(2.35rem,10vw,5.2rem)] font-black leading-[1.02] tracking-tight text-[#101726] dark:text-white">
                {t.home.hero.titleLine1}
                <br />
                {t.home.hero.titleLine2}{' '}
                <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-transparent dark:from-[#00bbc3] dark:to-[#00bbc3]">
                  {t.home.hero.titleHighlight}
                </span>
              </h1>
            </div>

            <p className="max-w-2xl text-sm font-medium leading-7 text-[#263246]/90 sm:text-lg sm:leading-8 lg:text-xl dark:text-white/80">
              {t.home.hero.description}
            </p>

            <div className="flex flex-wrap gap-3 pt-1 sm:gap-4 sm:pt-2">
              <Link
                to="/contact"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#007d9b] px-5 text-sm font-black text-white shadow-lg shadow-[#00bbc3]/20 transition-all hover:opacity-95 active:scale-95 sm:px-6 dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
              >
                {t.common.buttons.bookIntroduction} <ArrowRight size={18} />
              </Link>

              <button
                type="button"
                onClick={() => goToSection(1)}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-border-primary bg-background-card/40 px-5 text-sm font-black text-[#007d9b] transition-all hover:bg-[#00bbc3]/5 sm:px-6 dark:text-[#00bbc3] dark:hover:bg-[#00bbc3]/10"
              >
                {t.common.buttons.discoverExpertise} <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>

          <motion.div
            className="relative z-10 mx-auto hidden w-full max-w-6xl justify-center pb-2 md:flex"
            initial={shouldAnimateContent ? hiddenState(18) : false}
            animate={getSectionAnimationState(0, 18)}
            transition={sectionTransition(0.08)}
          >
            <button
              type="button"
              aria-label={t.home.hero.capabilitiesAria}
              onClick={() => goToSection(1)}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-border-primary bg-background-card/60 text-[#007d9b] transition-all hover:bg-[#00bbc3]/10 dark:border-white/10 dark:text-white/80"
            >
              <ArrowDown size={14} />
            </button>
          </motion.div>
        </section>

        {/* ===== SECTION 2: CAPABILITIES ===== */}
        <section
          id="services"
          className="relative h-[100dvh] overflow-hidden bg-[#f4f8fc] px-5 pb-16 pt-28 text-[#101726] sm:px-12 sm:py-24 dark:bg-[#090f1c] dark:text-white"
        >
          <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(221,240,250,.95)_0%,rgba(242,247,252,.96)_48%,rgba(210,230,245,.95)_100%)] dark:bg-[linear-gradient(115deg,#0c3240_0%,#101729_34%,#1b2742_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(14,27,52,.045)_1px,transparent_1px),linear-gradient(90deg,rgba(14,27,52,.045)_1px,transparent_1px)] bg-[size:100px_100px] dark:bg-[linear-gradient(rgba(255,255,255,.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.03)_1px,transparent_1px)]" />

          <SectionSoftEdge
            top="bg-gradient-to-b from-[#edf5ff]/60 to-transparent dark:from-[#10172b]/55 dark:to-transparent"
            bottom="bg-gradient-to-b from-transparent via-[#f4f8fc]/20 to-[#edf5ff]/70 dark:via-[#101729]/25 dark:to-[#091124]/75"
          />

          <div className="relative z-10 mx-auto flex h-full max-w-6xl flex-col justify-start space-y-4 sm:justify-center sm:space-y-8">
            <motion.div
              className="max-w-2xl text-left"
              initial={shouldAnimateContent ? hiddenState(18) : false}
              animate={getSectionAnimationState(1, 18)}
              transition={sectionTransition(0)}
            >
              <span className="text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] sm:text-xs dark:text-[#00bbc3]">
                {t.home.services.eyebrow}
              </span>
              <h2 className="mt-1 text-[clamp(2rem,9vw,3.5rem)] font-black leading-none tracking-tight">
                {t.home.services.title}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
              {servicesItems.map((service, index) => {
                const IconComponent = service.icon;

                return (
                  <motion.div
                    key={service.slug}
                    className="h-full"
                    initial={shouldAnimateContent ? cardHiddenState(24) : false}
                    animate={getCardAnimationState(1, 24)}
                    transition={cardLoadTransition(0.1 * index)}
                  >
                    <Link
                      to={`/services/${service.slug}`}
                      className="block h-full"
                    >
                      <motion.article
                        whileHover={hoverLift}
                        whileTap={tapPress}
                        className="group flex h-full min-h-[164px] cursor-pointer flex-col justify-between rounded-2xl border border-border-primary bg-background-card/60 p-4 shadow-xl backdrop-blur-xl transition-all hover:border-[#00bbc3]/40 sm:min-h-[240px] sm:rounded-[24px] sm:p-6 dark:border-white/10 dark:bg-white/5 dark:hover:border-[#00bbc3]/30"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#00bbc3]/10 text-[#007d9b] sm:h-11 sm:w-11 sm:rounded-2xl dark:text-[#00bbc3]">
                          <IconComponent size={19} strokeWidth={2.5} />
                        </span>

                        <div className="mt-3 space-y-1.5 sm:mt-6">
                          <h3 className="text-[15px] font-black leading-snug tracking-tight transition-colors group-hover:text-[#007d9b] sm:text-lg dark:group-hover:text-[#00bbc3]">
                            {service.title}
                          </h3>
                          <p className="text-[10px] font-medium leading-[1.45] text-text-secondary sm:text-xs sm:leading-normal">
                            {service.desc}
                          </p>
                        </div>
                      </motion.article>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===== SECTION 3: FEATURED CASE STUDIES ===== */}
        <section
          id="cases"
          className="relative h-[100dvh] overflow-hidden bg-[#edf5ff] px-5 pb-10 pt-24 text-[#101726] sm:px-8 md:px-10 md:pb-8 lg:px-12 lg:pb-10 2xl:py-24 dark:bg-[#091124] dark:text-white"
        >
          <div className="absolute inset-0 bg-[linear-gradient(115deg,#edf5ff_0%,#f7fbff_48%,#d9eef4_100%)] dark:bg-[linear-gradient(115deg,#091124_0%,#0c1425_48%,#060b14_100%)]" />
          <div className="pointer-events-none absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-[#00bbc3]/5 blur-[120px] dark:bg-[#00bbc3]/10" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(14,27,52,.045)_1px,transparent_1px),linear-gradient(90deg,rgba(14,27,52,.045)_1px,transparent_1px)] bg-[size:100px_100px] dark:bg-[linear-gradient(rgba(255,255,255,.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.015)_1px,transparent_1px)]" />

          <SectionSoftEdge
            top="bg-gradient-to-b from-[#f4f8fc]/60 to-transparent dark:from-[#090f1c]/55 dark:to-transparent"
            bottom="bg-gradient-to-b from-transparent via-[#edf5ff]/20 to-[#edf4fa]/70 dark:via-[#091124]/25 dark:to-[#070c16]/75"
          />

          <div className="relative z-10 mx-auto flex h-full max-w-6xl flex-col justify-start space-y-4 lg:space-y-5 2xl:justify-center 2xl:space-y-10">
            <motion.div
              className="grid items-end gap-3 border-b border-border-primary/60 pb-3 md:gap-4 md:pb-4 lg:grid-cols-12 2xl:gap-6 2xl:pb-6 dark:border-white/10"
              initial={shouldAnimateContent ? hiddenState(18) : false}
              animate={getSectionAnimationState(2, 18)}
              transition={sectionTransition(0)}
            >
              <h2 className="text-[clamp(1.7rem,4vw,2.75rem)] font-black leading-[1.02] tracking-tight text-[#101726] lg:col-span-5 2xl:text-[clamp(1.95rem,3.6vw,3.8rem)] dark:text-white">
                {t.home.cases.titleStart}{' '}
                <span className="bg-gradient-to-r from-[#00bbc3] to-cyan-400 bg-clip-text text-transparent">
                  {t.home.cases.titleHighlight}
                </span>
              </h2>

              <p className="text-xs font-medium leading-5 text-[#263246]/80 md:text-sm md:leading-6 lg:col-span-7 2xl:text-base 2xl:leading-7 dark:text-white/60">
                {t.home.cases.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 2xl:gap-6">
              {featuredCases.map((item, index) => {
                const IconComponent = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={shouldAnimateContent ? cardHiddenState(24) : false}
                    animate={getCardAnimationState(2, 24)}
                    transition={cardLoadTransition(index * 0.12)}
                    className="h-full"
                  >
                    <div className="group flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-border-primary bg-background-card/40 shadow-xl backdrop-blur-md transition-all duration-300 hover:border-[#00bbc3]/40 2xl:rounded-[28px] dark:border-white/5 dark:bg-white/[0.02] dark:hover:border-[#00bbc3]/30">
                      <div className="relative flex h-28 items-center justify-center overflow-hidden border-b border-border-primary/50 bg-white/85 px-5 dark:border-white/5 dark:bg-white/[0.06] 2xl:h-44">
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_15%,rgba(0,187,195,0.16),transparent_60%)]" />
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className={`relative h-20 max-h-full w-full max-w-[22rem] object-contain drop-shadow-sm 2xl:h-32 ${item.imageClassName ?? ''}`}
                            loading="lazy"
                          />
                        ) : (
                          <div className="relative flex flex-col items-center gap-3 text-center text-[#007d9b] dark:text-[#00bbc3]">
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#00bbc3]/25 bg-[#00bbc3]/10 2xl:h-16 2xl:w-16">
                              <IconComponent
                                className="h-6 w-6 2xl:h-[30px] 2xl:w-[30px]"
                                strokeWidth={2.4}
                              />
                            </span>
                            <span className="max-w-[15rem] text-[10px] font-black uppercase tracking-[0.24em] 2xl:text-xs">
                              {item.tag}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 flex-col justify-between p-4 2xl:p-6">
                        <div className="space-y-2 2xl:space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <span className="rounded-full bg-[#007d9b]/10 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-[#007d9b] sm:text-[10px] dark:bg-[#00bbc3]/10 dark:text-[#00bbc3]">
                              {item.tag}
                            </span>
                            <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-lg font-black text-transparent 2xl:text-2xl dark:from-cyan-400 dark:to-[#00bbc3]">
                              {item.metric}
                            </span>
                          </div>

                          <h3 className="pt-1 text-[15px] font-black leading-snug tracking-tight text-[#101726] 2xl:pt-2 2xl:text-xl dark:text-white">
                            {item.title}
                          </h3>

                          <p className="text-[11px] font-medium leading-5 text-text-secondary 2xl:text-sm 2xl:leading-relaxed dark:text-white/50">
                            {item.desc}
                          </p>
                        </div>

                        <div className="pt-3 2xl:pt-6">
                          <Link
                            to="/cases"
                            className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-[#007d9b] transition-colors hover:text-[#00bbc3] 2xl:text-xs dark:text-[#00bbc3] dark:hover:text-cyan-300"
                          >
                            {t.common.buttons.viewFullCaseStudy}{' '}
                            <ArrowRight size={13} strokeWidth={2.5} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={shouldAnimateContent ? hiddenState(24) : false}
              animate={getSectionAnimationState(2, 24)}
              transition={sectionTransition(0.24)}
              className="pt-1 text-center sm:text-left"
            >
              <Link
                to="/cases"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#007d9b] px-5 text-xs font-black text-white shadow-lg shadow-[#00bbc3]/20 transition-all hover:opacity-95 active:scale-95 sm:h-12 sm:px-6 sm:text-sm dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
              >
                {t.common.buttons.browseAllCaseStudies} <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ===== SECTION 4: ENGINEERING PRINCIPLES ===== */}
        <section
          id="approach"
          className="relative h-[100dvh] overflow-hidden bg-[#edf4fa] px-5 pb-16 pt-28 text-[#101726] sm:px-12 sm:py-24 dark:bg-[#070c16] dark:text-white"
        >
          <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(221,240,250,.95)_0%,rgba(242,247,252,.96)_48%,rgba(210,230,245,.95)_100%)] dark:bg-[linear-gradient(115deg,#111a31_0%,#0d1527_42%,#1d2944_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(14,27,52,.045)_1px,transparent_1px),linear-gradient(90deg,rgba(14,27,52,.045)_1px,transparent_1px)] bg-[size:100px_100px] dark:bg-[linear-gradient(rgba(255,255,255,.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.03)_1px,transparent_1px)]" />

          <SectionSoftEdge
            top="bg-gradient-to-b from-[#edf5ff]/60 to-transparent dark:from-[#091124]/55 dark:to-transparent"
            bottom="bg-gradient-to-b from-transparent via-[#edf4fa]/20 to-[#edf4fa]/70 dark:via-[#070c16]/25 dark:to-[#182241]/70"
          />

          <div className="relative z-10 mx-auto flex h-full max-w-6xl flex-col justify-start space-y-4 sm:justify-center sm:space-y-10">
            <motion.div
              className="border-b border-border-primary/60 pb-4 text-left dark:border-white/10"
              initial={shouldAnimateContent ? hiddenState(18) : false}
              animate={getSectionAnimationState(3, 18)}
              transition={sectionTransition(0)}
            >
              <span className="text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] sm:text-xs dark:text-[#00bbc3]">
                {t.home.approach.eyebrow}
              </span>
              <h2 className="mt-1 text-[clamp(1.65rem,7.2vw,3.5rem)] font-black leading-[1.02] tracking-tight">
                {t.home.approach.titleStart}{' '}
                <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-transparent dark:from-[#00bbc3] dark:to-[#00bbc3]">
                  {t.home.approach.titleHighlight}
                </span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
              {approachItems.map((item, index) => {
                const ItemIcon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={shouldAnimateContent ? cardHiddenState(24) : false}
                    animate={getCardAnimationState(3, 24)}
                    transition={cardLoadTransition(index * 0.1)}
                    className="h-full"
                  >
                    <motion.article
                      whileHover={{ y: -3 }}
                      className="flex h-full min-h-[168px] flex-col justify-between rounded-2xl border border-border-primary bg-background-card/50 p-4 shadow-md backdrop-blur-md sm:min-h-0 sm:p-5 dark:border-white/5 dark:bg-white/5"
                    >
                      <div className="space-y-3 sm:space-y-4">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#00bbc3]/20 bg-[#00bbc3]/10 text-[#007d9b] sm:h-10 sm:w-10 dark:text-[#00bbc3]">
                          <ItemIcon size={18} strokeWidth={2.5} />
                        </span>

                        <div className="space-y-1.5">
                          <h3 className="text-[13px] font-black leading-snug tracking-tight text-text-primary sm:text-base">
                            {item.title}
                          </h3>
                          <p className="text-[9.5px] font-medium leading-[1.4] text-text-secondary sm:text-xs sm:leading-normal">
                            {item.text}
                          </p>
                        </div>
                      </div>
                    </motion.article>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={shouldAnimateContent ? hiddenState(24) : false}
              animate={getSectionAnimationState(3, 24)}
              transition={sectionTransition(0.24)}
              className="pt-1"
            >
              <Link
                to="/about"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#007d9b] px-5 text-xs font-black text-white shadow-lg shadow-[#00bbc3]/20 transition-all hover:opacity-95 active:scale-95 sm:h-12 sm:px-6 sm:text-sm dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
              >
                {t.common.buttons.seeEngineeringStandards}{' '}
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ===== SECTION 5: FINAL CTA ===== */}
        <section
          id="contact"
          className="relative flex h-[100dvh] items-start justify-center overflow-hidden px-5 pb-16 pt-28 text-[#101726] sm:items-center sm:px-12 sm:py-24 dark:text-white"
        >
          <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(221,240,250,.95)_0%,rgba(242,247,252,.96)_48%,rgba(210,230,245,.95)_100%)] dark:bg-[linear-gradient(115deg,#182241_0%,#101729_42%,#0b3441_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(14,27,52,.045)_1px,transparent_1px),linear-gradient(90deg,rgba(14,27,52,.045)_1px,transparent_1px)] bg-[size:100px_100px] dark:bg-[linear-gradient(rgba(255,255,255,.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.03)_1px,transparent_1px)]" />

          <SectionSoftEdge top="bg-gradient-to-b from-[#edf4fa]/60 to-transparent dark:from-[#070c16]/55 dark:to-transparent" />

          <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-6 sm:gap-12 md:grid-cols-12">
            <motion.div
              className="space-y-4 text-left sm:space-y-6 md:col-span-6"
              initial={shouldAnimateContent ? hiddenState(18) : false}
              animate={getSectionAnimationState(4, 18)}
              transition={sectionTransition(0)}
            >
              <span className="text-[10px] font-black uppercase tracking-[0.26em] text-[#007d9b] sm:text-xs dark:text-[#00bbc3]">
                {t.home.cta.eyebrow}
              </span>

              <h2 className="text-[clamp(2.15rem,9.8vw,3.8rem)] font-black leading-[1.05] tracking-tight">
                {t.home.cta.titleLine1}
                <br /> {t.home.cta.titleLine2}{' '}
                <span className="bg-gradient-to-r from-[#007d9b] to-[#00bbc3] bg-clip-text text-transparent dark:from-[#00bbc3] dark:to-[#00bbc3]">
                  {t.home.cta.titleHighlight}
                </span>
                {t.home.cta.titleSuffix}
              </h2>

              <p className="max-w-md text-sm font-medium leading-6 text-text-secondary sm:text-base sm:leading-7">
                {t.home.cta.description}
              </p>

              <Link
                to="/contact"
                className="inline-flex h-12 items-center justify-center gap-3 rounded-xl bg-[#007d9b] px-6 text-sm font-black text-white shadow-xl shadow-[#00bbc3]/20 transition-all hover:opacity-95 active:scale-95 dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3]"
              >
                {t.common.buttons.bookIntroduction} <ArrowRight size={18} />
              </Link>
            </motion.div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:col-span-6">
              {techPartners.map((tech, index) => (
                <motion.div
                  key={tech.name}
                  initial={shouldAnimateContent ? cardHiddenState(24) : false}
                  animate={getCardAnimationState(4, 24)}
                  transition={cardLoadTransition(index * 0.1)}
                >
                  <motion.div
                    whileHover={{ y: -4, scale: 1.03 }}
                    className="flex h-[4.25rem] items-center justify-center rounded-xl border border-border-primary/80 bg-background-card/40 p-3 text-center text-xs font-black tracking-tight text-text-primary shadow-sm backdrop-blur-md transition-all hover:border-[#00bbc3]/30 sm:h-20 sm:p-4 sm:text-sm dark:border-white/5 dark:bg-white/1"
                  >
                    <span className="bg-gradient-to-r from-text-primary via-text-primary to-text-secondary bg-clip-text text-transparent dark:from-white dark:to-white/70">
                      {tech.name}
                    </span>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </motion.div>
    </main>
  );
};

export default HomePage;
